import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchChecklists,
  fetchChecklist,
  updateChecklist,
  createChecklist,
  deleteChecklist,
} from "../../api/quality/checklist";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  totalCount: 0,
  currentCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteChecklistAsync = createAsyncThunk(
  "checklists/deleteChecklistAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteChecklist(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_checklist");
    }
  }
);

export const createChecklistAsync = createAsyncThunk(
  "checklists/createChecklistAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createChecklist(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_checklist");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_checklist");
    }
  }
);

export const updateChecklistAsync = createAsyncThunk(
  "checklists/updateChecklistAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateChecklist(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_checklist");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_checklist");
    }
  }
);

export const getChecklistsAsync = createAsyncThunk(
  "checklists/getChecklistsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchChecklists(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        response?.error ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_checklists");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_checklists");
    }
  }
);

export const getChecklistAsync = createAsyncThunk(
  "checklists/getChecklistAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchChecklist(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_checklist");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_checklist");
    }
  }
);

const checklistSlice = createSlice({
  name: "checklists",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getChecklistsAsync.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        if (!action.meta?.arg?.append) {
          state.data = [];
          state.totalCount = 0;
        }
      })
      .addCase(getChecklistsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.meta?.arg?.append) {
          const existing = new Set((state.data || []).map((d) => d.id));
          const incoming = (action.payload.data || []).filter(
            (d) => !existing.has(d.id)
          );
          state.data = [...(state.data || []), ...incoming];
        } else {
          state.data = action.payload.data;
        }
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
        state.newestChecklistsCount = action.payload.newCount;
      })
      .addCase(getChecklistsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente sjekklistene.");
      })
      .addCase(updateChecklistAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateChecklistAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const index = draftState.data.findIndex(
            (type) => type.id === action.payload.id
          );
          draftState.data[index] = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(updateChecklistAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere sjekklisten.");
      })
      .addCase(createChecklistAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createChecklistAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.isLoading = false;
        });
      })
      .addCase(createChecklistAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette sjekklisten.");
      })
      .addCase(deleteChecklistAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteChecklistAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteChecklistAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette sjekklisten.");
      })
      .addCase(getChecklistAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getChecklistAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.checklist = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getChecklistAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente sjekklisten.");
      });
  },
});

export const { reducer } = checklistSlice;

export default checklistSlice;
